import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from './SafeAnimatePresence';
import {
  FiSearch, FiX, FiMessageSquare, FiClock, FiUser, FiUsers, FiShield, FiFileText, FiRefreshCw,
} from 'react-icons/fi';
import type { ChatThread, Channel, Amp } from '../../features/chat/chat.types';
import type { InsuredResult } from '../../features/chat/InsuredMentionDropdown';
import { fetchRecentHistoryForAmp, CHAT_ARCHIVED_EVENT } from '../../services/historyService';
import ArchiveChatButton from './ArchiveChatButton';
import '../../styles/SearchModal.css';

const SEARCH_INSUREDS_API = `${process.env.REACT_APP_MyAMP_Url}/api/ams/search-insureds`;

const PAGE_SIZE = 10;

type FilterTab = 'all' | 'history' | 'insured' | 'prospect' | 'carrier' | 'policy';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  threads: ChatThread[];
  channels: Channel[];
  amps: Amp[];
  onSelectThread: (id: string) => void;
  onSelectChannel: (id: string) => void;
  onSelectAmp: (id: string) => void;
  onSelectInsured: (insured: InsuredResult) => void;
}

const TABS: { id: FilterTab; label: string; icon: React.ReactNode }[] = [
  { id: 'all', label: 'All', icon: <FiSearch size={13} /> },
  { id: 'history', label: 'History', icon: <FiClock size={13} /> },
  { id: 'insured', label: 'Insured', icon: <FiUser size={13} /> },
  { id: 'prospect', label: 'Prospect', icon: <FiUsers size={13} /> },
  { id: 'carrier', label: 'Carrier', icon: <FiShield size={13} /> },
  { id: 'policy', label: 'Policy', icon: <FiFileText size={13} /> },
];

function insuredTypeOf(ins: InsuredResult): 'insured' | 'prospect' | 'carrier' | 'policy' {
  const t = (ins.typeName || '').toLowerCase();
  if (t === 'policy' || t === 'prospect' || t === 'carrier') return t;
  return 'insured';
}

function getInsuredIcon(ins: InsuredResult) {
  const type = insuredTypeOf(ins);
  if (type === 'policy') return <FiFileText size={14} className="search-modal__result-icon" />;
  if (type === 'prospect') return <FiUsers size={14} className="search-modal__result-icon" />;
  if (type === 'carrier') return <FiShield size={14} className="search-modal__result-icon" />;
  return <FiUser size={14} className="search-modal__result-icon" />;
}

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString();
}

const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  threads,
  channels,
  amps,
  onSelectThread,
  onSelectChannel,
  onSelectAmp,
  onSelectInsured,
}) => {
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [insuredResults, setInsuredResults] = useState<InsuredResult[]>([]);
  const [insuredLoading, setInsuredLoading] = useState(false);
  const [historySearchResults, setHistorySearchResults] = useState<ChatThread[]>([]);
  const [historySearchLoading, setHistorySearchLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const insuredDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const insuredRequestRef = useRef(0);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyRequestRef = useRef(0);

  // Browse mode — server-paginated, 10 at a time, same pattern as HistoryModal.
  const [browseItems, setBrowseItems] = useState<ChatThread[]>([]);
  const [browseTotalCount, setBrowseTotalCount] = useState(0);
  // An archived chat leaves this modal's own lists right away.
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (!id) return;
      setBrowseItems((prev) => {
        if (!prev.some((t) => t.id === id)) return prev;
        setBrowseTotalCount((n) => Math.max(0, n - 1));
        return prev.filter((t) => t.id !== id);
      });
      setHistorySearchResults((prev) => prev.filter((t) => t.id !== id));
    };
    window.addEventListener(CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(CHAT_ARCHIVED_EVENT, onArchived);
  }, []);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseLoadingMore, setBrowseLoadingMore] = useState(false);
  const browseOffsetRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Most recently accessed first, same order as HistoryModal.
  const loadFirstPage = useCallback(() => {
    setBrowseLoading(true);
    fetchRecentHistoryForAmp(undefined, undefined, undefined, PAGE_SIZE, 0, 'lastAccessed')
      .then(({ threads: page, totalCount }) => {
        setBrowseItems(page);
        setBrowseTotalCount(totalCount);
        browseOffsetRef.current = page.length;
      })
      .catch(() => {
        setBrowseItems([]);
        setBrowseTotalCount(0);
        browseOffsetRef.current = 0;
      })
      .finally(() => setBrowseLoading(false));
  }, []);

  const loadNextPage = useCallback(() => {
    if (browseLoadingMore || browseItems.length >= browseTotalCount) return;
    setBrowseLoadingMore(true);
    fetchRecentHistoryForAmp(undefined, undefined, undefined, PAGE_SIZE, browseOffsetRef.current, 'lastAccessed')
      .then(({ threads: page, totalCount }) => {
        setBrowseItems((prev) => [...prev, ...page]);
        setBrowseTotalCount(totalCount);
        browseOffsetRef.current += page.length;
      })
      .catch(() => {})
      .finally(() => setBrowseLoadingMore(false));
  }, [browseLoadingMore, browseItems.length, browseTotalCount]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveTab('all');
      setInsuredResults([]);
      setInsuredLoading(false);
      setHistorySearchResults([]);
      setHistorySearchLoading(false);
      loadFirstPage();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, loadFirstPage]);

  // Search chat history via the same recent-history API the sidebar's History
  // modal uses — debounced, and returns every match (backend applies no
  // Limit/Offset when SearchText is set). Empty query instead browses the
  // paginated `browseItems` list (loaded/extended by loadFirstPage/loadNextPage above).
  useEffect(() => {
    if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setHistorySearchResults([]);
      setHistorySearchLoading(false);
      return;
    }
    setHistorySearchLoading(true);
    historyDebounceRef.current = setTimeout(() => {
      const requestId = ++historyRequestRef.current;
      fetchRecentHistoryForAmp(undefined, undefined, trimmed, undefined, undefined, 'lastAccessed')
        .then(({ threads: results }) => {
          if (requestId !== historyRequestRef.current) return; // stale response
          setHistorySearchResults(results);
        })
        .catch(() => {
          if (requestId === historyRequestRef.current) setHistorySearchResults([]);
        })
        .finally(() => {
          if (requestId === historyRequestRef.current) setHistorySearchLoading(false);
        });
    }, 300);
    return () => {
      if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
    };
  }, [query]);

  // Search insureds/prospects/policies via the AMS API — debounced, and
  // fired regardless of active tab so results are ready for both "All" and
  // "Insured". A monotonic request id guards against an older, slower
  // response overwriting the results of a newer keystroke.
  useEffect(() => {
    if (insuredDebounceRef.current) clearTimeout(insuredDebounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setInsuredResults([]);
      setInsuredLoading(false);
      return;
    }
    setInsuredLoading(true);
    insuredDebounceRef.current = setTimeout(async () => {
      const requestId = ++insuredRequestRef.current;
      try {
        const authToken = localStorage.getItem('authToken') || '';
        const amsToken = localStorage.getItem('amsToken') || '';
        const res = await fetch(SEARCH_INSUREDS_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ searchText: trimmed, nc_token: amsToken }),
        });
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        if (requestId !== insuredRequestRef.current) return; // stale response
        setInsuredResults(Array.isArray(data) ? data : (data.insureds || []));
      } catch {
        if (requestId === insuredRequestRef.current) setInsuredResults([]);
      } finally {
        if (requestId === insuredRequestRef.current) setInsuredLoading(false);
      }
    }, 300);
    return () => {
      if (insuredDebounceRef.current) clearTimeout(insuredDebounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const lowerQuery = query.toLowerCase().trim();
  const isSearchingHistory = lowerQuery.length > 0;

  const showHistory = activeTab === 'all' || activeTab === 'history';

  // Infinite scroll — only relevant while browsing History (not searching),
  // and only while the History section is actually on screen.
  useEffect(() => {
    if (isSearchingHistory || !showHistory) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadNextPage();
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSearchingHistory, showHistory, loadNextPage]);

  // Threads created client-side this session (e.g. a chat just started) that
  // the server-paginated fetch above hasn't surfaced yet — always shown first,
  // same as HistoryModal, so a brand-new conversation doesn't vanish from view.
  const localOnlyThreads = useMemo(() => {
    const knownIds = new Set([...browseItems, ...historySearchResults].map((t) => t.id));
    return threads
      .filter((t) => !knownIds.has(t.id) && t.messages.length > 0)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [threads, browseItems, historySearchResults]);

  // Search mode → one unpaginated call returning every match; browse mode →
  // server-paginated list (plus any brand-new local-only threads up front).
  const filteredThreads = isSearchingHistory ? historySearchResults : [...localOnlyThreads, ...browseItems];
  const hasMoreToLoad = !isSearchingHistory && browseItems.length < browseTotalCount;

  // "All" shows a small mixed-type preview; each dedicated tab (Insured /
  // Prospect / Carrier / Policy) shows only its own type from the same
  // search-insureds response — no extra API calls needed per tab.
  const insuredTypeResults = insuredResults.filter((r) => insuredTypeOf(r) === 'insured');
  const prospectTypeResults = insuredResults.filter((r) => insuredTypeOf(r) === 'prospect');
  const carrierTypeResults = insuredResults.filter((r) => insuredTypeOf(r) === 'carrier');
  const policyTypeResults = insuredResults.filter((r) => insuredTypeOf(r) === 'policy');

  const isAnyInsuredTab =
    activeTab === 'all' ||
    activeTab === 'insured' ||
    activeTab === 'prospect' ||
    activeTab === 'carrier' ||
    activeTab === 'policy';

  const relevantInsuredCount =
    activeTab === 'insured' ? insuredTypeResults.length :
    activeTab === 'prospect' ? prospectTypeResults.length :
    activeTab === 'carrier' ? carrierTypeResults.length :
    activeTab === 'policy' ? policyTypeResults.length :
    insuredResults.length;

  const hasResults =
    (showHistory && filteredThreads.length > 0) ||
    (activeTab === 'all' && insuredResults.length > 0) ||
    (activeTab === 'insured' && insuredTypeResults.length > 0) ||
    (activeTab === 'prospect' && prospectTypeResults.length > 0) ||
    (activeTab === 'carrier' && carrierTypeResults.length > 0) ||
    (activeTab === 'policy' && policyTypeResults.length > 0);

  const renderInsuredRows = (list: InsuredResult[], cap: number) =>
    list.slice(0, cap).map((ins) => (
      <div
        key={ins.id}
        className="search-modal__result-item"
        onClick={() => onSelectInsured(ins)}
      >
        {getInsuredIcon(ins)}
        <div className="search-modal__result-info">
          <div className="search-modal__result-title">
            {ins.typeName === 'Policy' ? (ins.policyNumber || ins.name) : ins.name}
          </div>
          <div className="search-modal__result-meta">
            {ins.typeName}{ins.city ? ` · ${ins.city}${ins.stateAbv ? `, ${ins.stateAbv}` : ''}` : ''}
          </div>
        </div>
      </div>
    ));

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return (
    <SafeAnimatePresence>
      {isOpen && (
        <motion.div
          className="search-modal__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={handleBackdropClick}
        >
          <motion.div
            className="search-modal glass-surface"
            initial={{ opacity: 0, y: -16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Search input */}
            <div className="search-modal__header">
              <div className="search-modal__input-wrap">
                <FiSearch size={16} className="search-modal__input-icon" />
                <input
                  ref={inputRef}
                  className="search-modal__input"
                  placeholder="Search conversations, channels, messages..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button
                    className="search-modal__clear"
                    onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  >
                    <FiX size={14} />
                  </button>
                )}
              </div>
              <button className="search-modal__close" onClick={onClose}>
                <FiX size={18} />
              </button>
            </div>

            {/* Filter tabs */}
            <div className="search-modal__tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`search-modal__tab ${activeTab === tab.id ? 'search-modal__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            {/* Results */}
            <div className="search-modal__results">
              {!hasResults && lowerQuery.length > 0 && !(isAnyInsuredTab && insuredLoading) && !(showHistory && historySearchLoading) && (
                <div className="search-modal__empty">
                  No results for "{query}"
                </div>
              )}

              {!hasResults && lowerQuery.length === 0 && !(showHistory && browseLoading) && (
                <div className="search-modal__empty">
                  Type to search across history, insureds, and policies
                </div>
              )}

              {/* History results */}
              {showHistory && filteredThreads.length > 0 && (
                <div className="search-modal__section">
                  {(activeTab === 'all' && insuredResults.length > 0) && (
                    <div className="search-modal__section-label">
                      <FiClock size={12} /> History
                    </div>
                  )}
                  {filteredThreads.map((thread) => (
                    <div
                      key={thread.id}
                      className="search-modal__result-item has-archive-chat"
                      onClick={() => onSelectThread(thread.id)}
                    >
                      <FiMessageSquare size={14} className="search-modal__result-icon" />
                      <div className="search-modal__result-info">
                        <div className="search-modal__result-title">{thread.title}</div>
                        <div className="search-modal__result-meta">{formatRelativeTime(thread.updatedAt)}</div>
                      </div>
                      <ArchiveChatButton conversationId={thread.id} />
                    </div>
                  ))}

                  {/* Infinite-scroll sentinel + "loading more" — browse mode only */}
                  {hasMoreToLoad && (
                    <div ref={sentinelRef} className="search-modal__load-more">
                      {browseLoadingMore && <FiRefreshCw size={13} className="search-modal__spin" />}
                    </div>
                  )}
                </div>
              )}

              {showHistory && (historySearchLoading || browseLoading) && filteredThreads.length === 0 && (
                <div className="search-modal__section">
                  <div className="search-modal__empty search-modal__empty--loading">
                    {isSearchingHistory ? 'Searching history…' : 'Loading conversations…'}
                  </div>
                </div>
              )}

              {/* "All" — small mixed-type preview across insured/prospect/carrier/policy */}
              {activeTab === 'all' && insuredResults.length > 0 && (
                <div className="search-modal__section">
                  {filteredThreads.length > 0 && (
                    <div className="search-modal__section-label">
                      <FiUser size={12} /> Insured
                    </div>
                  )}
                  {renderInsuredRows(insuredResults, 5)}
                </div>
              )}

              {/* Dedicated tabs — each shows only its own type from the same search-insureds response */}
              {activeTab === 'insured' && insuredTypeResults.length > 0 && (
                <div className="search-modal__section">{renderInsuredRows(insuredTypeResults, 50)}</div>
              )}
              {activeTab === 'prospect' && prospectTypeResults.length > 0 && (
                <div className="search-modal__section">{renderInsuredRows(prospectTypeResults, 50)}</div>
              )}
              {activeTab === 'carrier' && carrierTypeResults.length > 0 && (
                <div className="search-modal__section">{renderInsuredRows(carrierTypeResults, 50)}</div>
              )}
              {activeTab === 'policy' && policyTypeResults.length > 0 && (
                <div className="search-modal__section">{renderInsuredRows(policyTypeResults, 50)}</div>
              )}

              {isAnyInsuredTab && insuredLoading && relevantInsuredCount === 0 && (
                <div className="search-modal__section">
                  <div className="search-modal__empty search-modal__empty--loading">Searching…</div>
                </div>
              )}
            </div>

            {/* Footer hint */}
            <div className="search-modal__footer">
              <span>Press <kbd>Esc</kbd> to close</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default React.memo(SearchModal);
